import type { EnvVarType, ProcessEnvVar } from '@stratumn/studio-shared';
import { Loader2 } from 'lucide-react';
import { observer } from 'mobx-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  EnvVarCellCenter,
  EnvVarCellInput,
  EnvVarCellMuted,
  EnvVarCellText,
  EnvVarTable
} from '@/components/env-var/EnvVarTable';
import { EnvVarValueCell } from '@/components/env-var/EnvVarValueCell';
import { Button } from '@/components/ui/button';
import {
  SettingHeader,
  SettingsContainer,
  SettingsContent
} from '@/components/ui/settings';
import { Switch } from '@/components/ui/switch';
import useStores from '@/hooks/useStore';
import { ParamsList } from '@/routes/routes.types';
import { extractErrorMessage } from '@/services/errors/errors';

type DraftSharedVar = {
  envVarId: string;
  name: string;
  type: EnvVarType;
  value: string;
  enabled: boolean;
};

const buildDrafts = (envVars: ProcessEnvVar[]): DraftSharedVar[] =>
  envVars
    .filter(v => v.shared)
    .map(v => ({
      envVarId: v.id,
      name: v.name,
      type: v.type,
      value: v.value,
      enabled: v.enabled
    }));

export const EnvironmentPage = observer(() => {
  const processId = useParams()[ParamsList.ProcessId];
  const { envVarStore } = useStores();
  const [drafts, setDrafts] = useState<DraftSharedVar[]>([]);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (processId) {
      envVarStore.fetchEnvVars(processId).catch(() => {
        // error already reported via newError → Sentry; suppress unhandled rejection
      });
    }
  }, [envVarStore, processId]);

  useEffect(() => {
    // Only re-sync from the canonical store when the user has no in-flight
    // edits. Otherwise we would clobber unsaved value/enabled changes the
    // moment any sibling code path (sibling page mount, processLayout
    // re-fetch, etc.) re-fetches env vars.
    if (dirty) {
      return;
    }
    setDrafts(buildDrafts(envVarStore.envVars));
  }, [envVarStore.envVars, dirty]);

  const sharedVars = useMemo(
    () => envVarStore.envVars.filter(v => v.shared),
    [envVarStore.envVars]
  );

  const updateDraft = useCallback(
    (index: number, patch: Partial<DraftSharedVar>) => {
      setDrafts(prev => {
        const next = [...prev];
        next[index] = { ...next[index], ...patch };
        return next;
      });
      setDirty(true);
    },
    []
  );

  const handleSave = useCallback(async () => {
    if (!processId) {
      return;
    }

    setSaving(true);
    try {
      for (const draft of drafts) {
        const original = sharedVars.find(v => v.id === draft.envVarId);
        if (
          original &&
          (original.value !== draft.value || original.enabled !== draft.enabled)
        ) {
          await envVarStore.updateEnvVar(processId, draft.envVarId, {
            value: draft.value,
            enabled: draft.enabled
          });
        }
      }
      toast.success('Environment variables saved');
      setDirty(false);
    } catch (error: unknown) {
      toast.error(`Failed to save: ${extractErrorMessage(error)}`);
    } finally {
      setSaving(false);
    }
  }, [drafts, envVarStore, processId, sharedVars]);

  const renderBody = () => {
    if (envVarStore.loading) {
      return (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="size-5 animate-spin text-muted-foreground" />
        </div>
      );
    }
    if (drafts.length === 0) {
      return (
        <p className="py-8 text-center text-muted-foreground text-sm">
          No shared environment variables defined for this process.
        </p>
      );
    }
    return (
      <div className="rounded-xl bg-card p-6 shadow-verail">
        <h3 className="mb-1 font-medium text-lg">Environment Variables</h3>
        <p className="mb-4 text-muted-foreground text-sm">
          Edit the values of shared environment variables for this process.
        </p>
        <EnvVarTable
          headers={[
            { label: 'Name' },
            { label: 'Type' },
            { label: 'Value' },
            { label: 'Enabled', center: true }
          ]}
        >
          {drafts.map((draft, index) => (
            <tr key={draft.envVarId}>
              <EnvVarCellText>{draft.name}</EnvVarCellText>
              <EnvVarCellMuted>{draft.type}</EnvVarCellMuted>
              <EnvVarCellInput>
                <EnvVarValueCell
                  onChange={value => updateDraft(index, { value })}
                  type={draft.type}
                  value={draft.value}
                />
              </EnvVarCellInput>
              <EnvVarCellCenter>
                <Switch
                  checked={draft.enabled}
                  onCheckedChange={v => updateDraft(index, { enabled: v })}
                />
              </EnvVarCellCenter>
            </tr>
          ))}
        </EnvVarTable>
        <div className="mt-4 flex justify-end">
          <Button
            disabled={!dirty || saving}
            onClick={() => void handleSave()}
            size="sm"
          >
            {saving && <Loader2 className="mr-2 size-4 animate-spin" />}
            Save
          </Button>
        </div>
      </div>
    );
  };

  return (
    <SettingsContainer>
      <SettingHeader
        description="View and edit shared environment variable values for this process"
        title="Variables"
      />
      <SettingsContent className="gap-6 self-start pb-8">
        {renderBody()}
      </SettingsContent>
    </SettingsContainer>
  );
});
